'use client';

import {getOfferKeyImages, getOfferStoreUrl} from '@/lib/utils/catalog.utils';
import {AnalyticEventProps, KeyImageType} from '@/types';
import {Button, Card, CardProps, Stack, Text} from '@eds-web/react';
import {css} from '@panda/css';
import {useCatalogPriceEngine} from './useCatalogPriceEngine';
import {useAccountOfferSubscription, useQuickLinks} from '@/hooks';
import {Box} from '@panda/jsx';
import {useEmitEventOnClick} from '@/lib/utils/analytics';
import {getOnKeyDownProp} from '@/lib/utils/a11y';
import {CatalogOffer} from 'epic-graphql-types';
import {getMessage} from '@/lib/utils/i18n.utils';
import {useMessages} from '@epic-mw/i18n';
import {ReactNode} from 'react';

const mediaQuery = 'md';

const buttonStyles = css({
	pointerEvents: 'none', // cosmetic only, card registers click.
	minW: '159px',
	textDecoration: 'none !important',
	'&:hover': {
		color: 'black !important'
	}
});

const contentStyles = css({
	[mediaQuery]: {
		width: '100%',
		padding: '8px !important' // Allow more room for product image's 16:9 object-fit display on desktop.
	},
	padding: '16px !important',
	textAlign: 'center',
	justifyContent: 'center !important'
});

const imageStyles = css({
	[mediaQuery]: {
		maxWidth: '50%',
		borderTopRightRadius: '0 !important',
		borderBottomLeftRadius: 'eds.borderRadius.8 !important'
	}
});

const cardStyles = css({
	cursor: 'pointer',
	bg: 'eds.background.elevated.high.default !important',
	[mediaQuery]: {display: 'flex !important'}
});

interface CatalogSubOfferCardProps {
	cardProps: CardProps;
	offer: CatalogOffer;
	analytics?: AnalyticEventProps;
}

export const CatalogSubOfferCard = ({cardProps, offer, analytics}: CatalogSubOfferCardProps) => {
	const offerId = offer?.id || '';
	const sub = useAccountOfferSubscription(offerId);
	const subscribed = sub.data?.subscribed ?? false;
	const price = useCatalogPriceEngine(offer);
	const quickLinks = useQuickLinks('accountSubscriptions');
	const images = getOfferKeyImages(offer?.keyImages, [
		KeyImageType.OFFER_IMAGE_WIDE,
		KeyImageType.PRODUCT_LOGO
	]);
	const thumbnail = images.get(KeyImageType.OFFER_IMAGE_WIDE);
	const productLogo = images.get(KeyImageType.PRODUCT_LOGO);
	const href = subscribed ? quickLinks.accountSubscriptions : getOfferStoreUrl(offer)! || '';

	const messages = useMessages();
	const ctaLabel = messages[subscribed ? 'epic.app.manage_subscription' : 'epic.app.subscribe'];

	const priceTransform = (price: ReactNode) => {
		return getMessage(messages, 'epic.app.spend.fnCrew.price', [price]);
	};

	const emitEvent = useEmitEventOnClick({
		...analytics,
		eventValue: {
			...analytics?.eventValue,
			status: 'initiate',
			offerId,
			sandboxId: offer?.namespace,
			href: href,
			thumbnailUrl: thumbnail?.url
		}
	});

	const onKeyDown = getOnKeyDownProp(() => {
		emitEvent();
		window.location.href = href;
	});

	return (
		<Card
			{...cardProps}
			className={cardStyles}
			imagesClassName={imageStyles}
			contentClassName={contentStyles}
			variant="solid"
			imageMode="single"
			imageAspectRatio="16:9"
			onKeyDown={onKeyDown}
			clickOverlayProps={{tabIndex: 0, href, onClick: emitEvent}}
			images={
				thumbnail ? <img alt={thumbnail.alt!} src={thumbnail.url!} /> : cardProps.images
			}
			title={
				<Stack gap={24} className={css({alignItems: 'center !important'})}>
					{productLogo && (
						<Box
							className={css({
								width: '100%',
								maxWidth: '186px',
								height: '52px',
								position: 'relative',
								overflow: 'hidden',
								aspectRatio: 93 / 26
							})}>
							<img
								src={productLogo.url!}
								alt={productLogo.alt!}
								className={css({
									width: '100%',
									position: 'absolute',
									top: '-67px',
									left: 0
								})}
							/>
						</Box>
					)}
					{offer.title}
				</Stack>
			}
			description={
				<Stack gap={8}>
					<p
						className={css({
							paddingTop: 'eds.dimension.4',
							fontSize: 'eds.font.12',
							lineHeight: 'eds.lineHeight.130',
							maxWidth: '469px',
							margin: '0 auto'
						})}>
						{offer.description}
					</p>
					{price && <Text variant="uiLGMedium">{priceTransform?.(price) || price}</Text>}
				</Stack>
			}
			customContent={
				<Box pt="24px">
					<Button size="lg" as="a" className={buttonStyles} href={href}>
						{ctaLabel}
					</Button>
				</Box>
			}
		/>
	);
};
